import React, { useState, useEffect, useContext, useCallback } from "react";
import { truncateWords } from "../../components/truncateWords";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import CashierApi from "../../utility/CashierApi";
import { CustomerContext } from "../CashierHomepage";
import { CustomerIdContext } from "../../context/CustomerIdProvider";
import ImageWithPlaceholder from "../../components/ImageWithPlaceholder";
import { addToOfflineCart } from "../../utility/offline/offlineCart";
import { isOffline } from "../../utility/offline/reachability";

function ProductCard({ item }) {
  const { createData } = CashierApi();
  const queryClient = useQueryClient();
  const { setSearched, currency } = useContext(CustomerContext);
  const { selectedCustomerId } = useContext(CustomerIdContext);

  const [formData, setFormData] = useState({
    customer_id: [selectedCustomerId],
    product_id: item.id,
    price: Number(item.price),
    quantity: 1,
    product_name: item.product_name,
    product: {
      product_name: item.product_name,
    },
    ...item,
  });

  useEffect(() => {
    setFormData((prev) => ({ ...prev, customer_id: selectedCustomerId }));
  }, [selectedCustomerId]);

  const mutation = useMutation({
    mutationFn: async (newCart) => {
      const addLocally = () =>
        addToOfflineCart(
          selectedCustomerId,
          queryClient.getQueryData(["cart", selectedCustomerId]),
          newCart
        );
      // Mid-session offline: keep selling from a local cart.
      if (isOffline()) return addLocally();
      try {
        return await createData("carts/store", newCart, true);
      } catch (error) {
        if (error?.isNetworkError) return addLocally();
        throw error;
      }
    },
    onMutate: async (newCart) => {
      // console.log({ newCart });
      await queryClient.cancelQueries("cart"); // Cancel any outgoing refetches

      const previousCartData = queryClient.getQueryData([
        "cart",
        selectedCustomerId,
      ]);

      if (
        previousCartData.filter((x) => x.product_id === newCart.product_id)
          .length > 0
      ) {
        return { previousCartData };
      }
      queryClient.setQueryData(["cart", selectedCustomerId], (old) => [
        ...old,
        newCart,
      ]); // Optimistically update the todos

      return { previousCartData };
    },
    onError: (error, newCart, context) => {
      queryClient.setQueryData(
        ["cart", selectedCustomerId],
        context.previousCartData
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cart", selectedCustomerId] });
      setSearched("");
    },
  });

  const handleClick = useCallback(
    (e) => {
      e.preventDefault();
      mutation.mutate({ ...formData, title: "cart add" }, {});
    },
    [formData, mutation]
  );

  return (
    <button
      disabled={mutation.isPending}
      onClick={handleClick}
      className="flex flex-col p-1 gap-2 bg-gray-200  hover:bg-gray-300 shadow-md items-center flex-none cursor-pointer rounded-md transition-transform duration-150 ease-in-out active:scale-95 active:bg-gray-300 active:shadow-lg"
    >
      <ImageWithPlaceholder
        item={item}
        className="w-24 h-24 lg:w-32 lg:h-32 rounded-md object-cover"
        placeholderClassName="w-24 h-24 lg:w-32 lg:h-32 bg-slate-400 text-white flex justify-center font-medium items-center rounded-md text-5xl"
      />

      <div className="text-center">
        <h3 className="text-sm">{truncateWords(item?.product_name, 20)}</h3>
        <h4 className="text-sm font-medium">
          {currency}
          {Number(item?.price).toLocaleString()}
        </h4>
      </div>
    </button>
  );
}

export default React.memo(ProductCard);
